import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Inbox, Mail, Lock, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, fmtErr } from "../lib/api";
import { LeaOrb } from "../components/Lea";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Auth({ mode }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login, register, googleLogin } = useAuth();
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const isLogin = mode === "login";
  const isForgot = mode === "forgot";
  const isReset = mode === "reset";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isForgot) {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success(t("auth.resetSent"));
      } else if (isReset) {
        await api.post("/auth/reset-password", { token: params.get("token"), password: form.password });
        toast.success(t("auth.passwordUpdated"));
        navigate("/login");
      } else {
        const user = isLogin
          ? await login(form.email, form.password)
          : await register(form.name, form.email, form.password);
        // Reprise de l'intention d'offre choisie sur la landing (validation serveur ensuite)
        const intent = sessionStorage.getItem("elvarify-intent-plan");
        if (intent && user.onboarding_done) navigate(`/app/compte?subscribe=${intent}`);
        else navigate(user.onboarding_done ? "/app" : "/onboarding");
      }
    } catch (err) {
      toast.error(fmtErr(err, t("auth.authFailed")));
    } finally {
      setBusy(false);
    }
  };

  const titles = {
    login: t("auth.titles.login"),
    register: t("auth.titles.register"),
    forgot: t("auth.titles.forgot"),
    reset: t("auth.titles.reset"),
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="auth-page">
      <div className="hidden lg:flex flex-col justify-between p-14 bg-primary text-white relative overflow-hidden">
        <div className="absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-electric/25 blur-3xl" />
        <div className="flex items-center justify-between relative">
          <Link to="/" className="flex items-center gap-2" data-testid="auth-logo">
            <div className="h-8 w-8 rounded-lg bg-electric flex items-center justify-center">
              <Inbox size={16} className="text-white" />
            </div>
            <span className="font-display font-bold text-lg">{t("common.appName")}</span>
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="relative">
          <h1 className="font-display font-black text-5xl leading-tight tracking-tighter mb-6">
            Less inbox.<br /><span className="font-serif-display italic font-normal text-electric">More action.</span>
          </h1>
          <p className="text-white/60 max-w-md text-sm leading-relaxed">
            {t("auth.panelSub")}
          </p>
        </div>
        <div className="relative flex items-center gap-3 text-white/60 text-xs">
          <LeaOrb size={24} />
          <span>{t("auth.panelLea")}</span>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="w-full max-w-md">
          <h2 className="font-display font-black text-4xl tracking-tighter mb-2" data-testid="auth-title">{titles[mode][0]}</h2>
          <p className="text-muted-foreground text-sm mb-10">{titles[mode][1]}</p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("auth.namePlaceholder")} data-testid="auth-name-input"
                  className="w-full rounded-xl border border-border bg-card pl-11 pr-4 py-3.5 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow" />
              </div>
            )}
            {!isReset && (
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t("auth.emailPlaceholder")} data-testid="auth-email-input"
                  className="w-full rounded-xl border border-border bg-card pl-11 pr-4 py-3.5 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow" />
              </div>
            )}
            {!isForgot && (
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input required type="password" minLength={8} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={isReset ? t("auth.newPasswordPlaceholder") : t("auth.passwordPlaceholder")} data-testid="auth-password-input"
                  className="w-full rounded-xl border border-border bg-card pl-11 pr-4 py-3.5 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow" />
              </div>
            )}
            <button type="submit" disabled={busy} data-testid="auth-submit-button"
              className="w-full rounded-full bg-electric text-white py-3.5 text-sm font-bold hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {busy ? t("auth.wait") : isLogin ? t("auth.login") : mode === "register" ? t("auth.register") : isForgot ? t("auth.sendLink") : t("auth.update")}
              {!busy && <ArrowRight size={15} />}
            </button>
          </form>

          {(isLogin || mode === "register") && (
            <>
              <div className="flex items-center gap-4 my-6">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">{t("auth.or")}</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <button onClick={googleLogin} data-testid="google-login-button"
                className="w-full rounded-full border border-border bg-card py-3.5 text-sm font-semibold hover:border-electric/50 transition-colors flex items-center justify-center gap-3">
                <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                {t("auth.google")}
              </button>
            </>
          )}

          <div className="mt-8 text-center text-xs text-muted-foreground space-y-2">
            {isLogin && (
              <>
                <p><Link to="/forgot-password" className="text-electric hover:underline" data-testid="forgot-link">{t("auth.forgot")}</Link></p>
                <p>{t("auth.noAccount")} <Link to="/register" className="text-electric hover:underline" data-testid="register-link">{t("auth.tryElvarify")}</Link></p>
              </>
            )}
            {mode === "register" && <p>{t("auth.hasAccount")} <Link to="/login" className="text-electric hover:underline" data-testid="login-link">{t("auth.login")}</Link></p>}
            {(isForgot || isReset) && <p><Link to="/login" className="text-electric hover:underline">{t("auth.backToLogin")}</Link></p>}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
